/**
 * UI strings for the web and desktop clients.
 *
 * The Telegram bot keeps its own catalogue because its wording is tuned for a
 * chat; this one is tuned for a screen. Both are keyed the same way so a
 * translator can work across them.
 */

import type { CalendarSystem, Lang } from "@calendar/shared";

type Dict = Record<string, string>;

const fa: Dict = {
  "app.title": "تقویم هوشمند",
  "nav.year": "سال",
  "nav.month": "ماه",
  "nav.week": "هفته",
  "nav.list": "فهرست",
  "nav.search": "جستجو",
  "nav.settings": "تنظیمات",
  "nav.today": "امروز",

  "action.new": "جلسهٔ جدید",
  "action.save": "ذخیره",
  "action.cancel": "انصراف",
  "action.delete": "حذف",
  "action.edit": "ویرایش",
  "action.close": "بستن",
  "action.retry": "تلاش دوباره",
  "action.logout": "خروج از حساب",
  "action.prev": "قبلی",
  "action.next": "بعدی",
  "action.attach": "افزودن پیوست",
  "action.addCategory": "دستهٔ جدید",
  "action.add": "افزودن",

  "field.title": "عنوان",
  "field.titlePlaceholder": "جلسهٔ هماهنگی",
  "field.date": "تاریخ",
  "field.start": "ساعت شروع",
  "field.end": "ساعت پایان",
  "field.allDay": "تمام‌روز",
  "field.location": "مکان",
  "field.notes": "یادداشت",
  "field.notesPlaceholder": "یادداشت، لینک یا توضیحات…",
  "field.category": "دسته",
  "field.none": "بدون دسته",
  "field.status": "وضعیت",
  "field.reminder": "یادآور",
  "field.displayName": "نام نمایشی",

  "quickAdd": "افزودن سریع",
  "quickAddPlaceholder": "جلسه فردا ساعت ۱۰ با علی",
  "quickAddHint": "یک جملهٔ عادی بنویسید؛ تاریخ و ساعت خودکار تشخیص داده می‌شود.",
  "quickAddNoDate": "تاریخی پیدا نشد. «فردا»، «امروز» یا «۱۵ مهر» بنویسید.",

  "status.confirmed": "تأیید‌شده",
  "status.tentative": "موقت",
  "status.cancelled": "لغو‌شده",
  "status.done": "انجام‌شده",

  "reminder.none": "بدون یادآور",
  "reminder.min": "دقیقه قبل",
  "reminder.hour": "ساعت قبل",
  "reminder.day": "روز قبل",
  "reminder.fired": "🔔 یادآوری:",

  "ics.export": "خروجی گرفتن (ICS)",
  "ics.import": "وارد کردن (ICS)",
  "ics.hint": "همهٔ رویدادها در یک فایل .ics دانلود می‌شوند و با کلیک روی «وارد کردن» می‌توانید فایلی از هر تقویم دیگری را بخوانید.",
  "ics.exported": "تعداد رویدادها در فایل: {n}",
  "ics.empty": "این فایل رویدادی نداشت.",
  "ics.imported": "تعداد {n} رویداد وارد شد.",
  "ics.partial": "از یک‌جا {n} رویداد وارد شد.",
  "ics.failed": "هیچ رویدادی وارد نشد.",

  "settings.language": "زبان",
  "settings.timezone": "منطقهٔ زمانی",
  "settings.calendar": "تقویم",
  "settings.categories": "دسته‌بندی‌ها",
  "settings.account": "اتصال به ربات",
  "settings.code": "کد اتصال",
  "settings.codeHint": "در تلگرام کلمهٔ «اتصال» را بفرستید و کد را اینجا وارد کنید.",
  "settings.apiKey": "کلید API",

  "calendar.jalali": "شمسی",
  "calendar.gregorian": "میلادی",
  "calendar.hijri": "قمری",

  "link.title": "اتصال به تقویم",
  "link.needCode": "برای شروع باید حساب خود را وصل کنید.",
  "link.needKey": "کلید API را وارد کنید.",
  "link.submit": "اتصال",
  "link.success": "حساب شما وصل شد.",
  "link.failed": "اتصال ناموفق بود.",
  "link.create": "ساخت کد اتصال",
  "link.ready": "این کد را در تلگرام وارد کنید.",

  "year.hint": "روی هر روز کلیک کنید تا به ماه آن بروید. تاریخ تقویم فعال درشت است و دو تقویم دیگر در گوشه‌ها می‌نشینند. تعطیلات رسمی قرمز نشان داده می‌شوند.",

  "empty.noEvents": "در این بازه رویدادی نیست.",
  "empty.noResults": "نتیجه‌ای پیدا نشد.",
  "empty.noNotes": "یادداشتی با این عبارت نیست.",
  "search.placeholder": "جستجو در عنوان و یادداشت‌ها…",
  "loading": "در حال بارگذاری…",
  "error.generic": "خطایی رخ داد.",
  "error.offline": "ارتباط با سرور برقرار نشد.",
  "notes.results": "نتایج جستجو در یادداشت‌ها",
};

const en: Dict = {
  "app.title": "Smart Calendar",
  "nav.year": "Year",
  "nav.month": "Month",
  "nav.week": "Week",
  "nav.list": "List",
  "nav.search": "Search",
  "nav.settings": "Settings",
  "nav.today": "Today",

  "action.new": "New event",
  "action.save": "Save",
  "action.cancel": "Cancel",
  "action.delete": "Delete",
  "action.edit": "Edit",
  "action.close": "Close",
  "action.retry": "Retry",
  "action.logout": "Sign out",
  "action.prev": "Previous",
  "action.next": "Next",
  "action.attach": "Attach file",
  "action.addCategory": "New category",
  "action.add": "Add",

  "field.title": "Title",
  "field.titlePlaceholder": "Team sync",
  "field.date": "Date",
  "field.start": "Start",
  "field.end": "End",
  "field.allDay": "All day",
  "field.location": "Location",
  "field.notes": "Notes",
  "field.notesPlaceholder": "Notes, links, details…",
  "field.category": "Category",
  "field.none": "No category",
  "field.status": "Status",
  "field.reminder": "Reminder",
  "field.displayName": "Display name",

  "quickAdd": "Quick add",
  "quickAddPlaceholder": "meeting tomorrow at 10 with Ali",
  "quickAddHint": "Write a plain sentence; the date and time are detected for you.",
  "quickAddNoDate": "No date found. Try “tomorrow”, “today” or “Sep 29”.",

  "status.confirmed": "Confirmed",
  "status.tentative": "Tentative",
  "status.cancelled": "Cancelled",
  "status.done": "Done",

  "reminder.none": "No reminder",
  "reminder.min": "minutes before",
  "reminder.hour": "hour before",
  "reminder.day": "day before",
  "reminder.fired": "🔔 Reminder:",

  "ics.export": "Export (.ics)",
  "ics.import": "Import (.ics)",
  "ics.hint": "All events download as a single .ics file, and you can read a file from any other calendar by clicking Import.",
  "ics.exported": "{n} events written to the file.",
  "ics.empty": "This file contained no events.",
  "ics.imported": "{n} events imported.",
  "ics.partial": "Imported {n} events.",
  "ics.failed": "No events could be imported.",

  "settings.language": "Language",
  "settings.timezone": "Time zone",
  "settings.calendar": "Calendar",
  "settings.categories": "Categories",
  "settings.account": "Bot link",
  "settings.code": "Link code",
  "settings.codeHint": "Send “link” in Telegram, then enter the code here.",
  "settings.apiKey": "API key",

  "calendar.jalali": "Solar Hijri",
  "calendar.gregorian": "Gregorian",
  "calendar.hijri": "Hijri",

  "link.title": "Link your account",
  "link.needCode": "Link your account to get started.",
  "link.needKey": "Enter your API key.",
  "link.submit": "Link",
  "link.success": "Your account is linked.",
  "link.failed": "Linking failed.",
  "link.create": "Create a link code",
  "link.ready": "Enter this code in Telegram to finish.",

  "year.hint": "Click a day to jump to its month. The active calendar is the large numeral and the other two sit in the corners, swapping as you switch. Official holidays are shown in red.",

  "empty.noEvents": "Nothing scheduled in this range.",
  "empty.noResults": "No results.",
  "empty.noNotes": "No notes match that text.",
  "search.placeholder": "Search titles and notes…",
  "loading": "Loading…",
  "error.generic": "Something went wrong.",
  "error.offline": "Could not reach the server.",
  "notes.results": "Notes search results",
};

const catalogues: Record<Lang, Dict> = { fa, en };

/** Exposed so a test can prove the two languages stay in step. */
export function catalogueKeys(lang: Lang): string[] {
  return Object.keys(catalogues[lang]).sort();
}

export function ui(lang: Lang, key: string, vars: Record<string, string | number> = {}): string {
  let out = catalogues[lang][key] ?? en[key] ?? key;
  for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v));
  return out;
}

export const UI_LANGS: Lang[] = ["fa", "en"];
export const CALENDARS: CalendarSystem[] = ["jalali", "gregorian", "hijri"];
